import { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSortFromUrl } from '@/hooks';
import { useSourcesStore } from '@/stores';
import { SortOption } from '@/utils/query-filters';
import type { FullSource } from '@/types/source';
import { SourceType } from '@/types/source';

/**
 * The default event sort depends on the selected source: file sources read
 * best oldest-first (chronological playback), everything else newest-first.
 *
 * Both the reader (this page's query) and the writer (the browse bar) rely on
 * this same default so that selecting the default value clears the `sort` URL
 * param without any effect needing to re-write it.
 */
export function useEventsSortDefault(): SortOption {
    const [searchParams] = useSearchParams();
    const sourcesParam = searchParams.get('sources');
    const getSourceById = useSourcesStore((state) => state.getSourceById);

    const singleSourceId = useMemo(() => {
        if (!sourcesParam || sourcesParam === 'all') return undefined;
        const ids = sourcesParam
            .split(',')
            .filter(Boolean)
            .map((id) => decodeURIComponent(id));
        return ids.length === 1 ? ids[0] : undefined;
    }, [sourcesParam]);

    const source = singleSourceId ? getSourceById(singleSourceId) : undefined;

    return source?.type === SourceType.FILE
        ? SortOption.DATE_OLDEST
        : SortOption.DATE_NEWEST;
}

interface EventsPageFiltersResult {
    sourceIds: string[];
    sourceId: string | undefined;
    source: FullSource | undefined;
    eventId: string | undefined;
    vehicleId: string | undefined;
    personId: string | undefined;
    hasSourceFilter: boolean;
    sortOption: SortOption;
    setSearchParams: ReturnType<typeof useSearchParams>[1];
}

export function useEventsPageFilters(): EventsPageFiltersResult {
    const [searchParams, setSearchParams] = useSearchParams();
    const sourcesParam = searchParams.get('sources');
    const sourceFoldersParam = searchParams.get('source_folders');
    const sourceIds = useMemo(() => {
        if (!sourcesParam || sourcesParam === 'all') return [];
        return sourcesParam
            .split(',')
            .filter(Boolean)
            .map((id) => decodeURIComponent(id));
    }, [sourcesParam]);
    const sourceId = sourceIds.length === 1 ? sourceIds[0] : undefined;
    const eventId = searchParams.get('eventId') ?? undefined;
    const vehicleId = searchParams.get('vehicleId') ?? undefined;
    const personId = searchParams.get('personId') ?? undefined;

    const defaultSortOption = useEventsSortDefault();
    const [sortOption] = useSortFromUrl(defaultSortOption);
    const { setSelectedSources, clearSelection, getSourceById } =
        useSourcesStore();

    const source = sourceId ? getSourceById(sourceId) : undefined;

    useEffect(() => {
        if (sourceFoldersParam) return;
        if (sourceIds.length > 0) {
            setSelectedSources(new Set(sourceIds));
        } else {
            clearSelection();
        }
    }, [sourceFoldersParam, sourceIds, setSelectedSources, clearSelection]);

    return {
        sourceIds,
        sourceId,
        source,
        eventId,
        vehicleId,
        personId,
        hasSourceFilter: sourceIds.length > 0 || Boolean(sourceFoldersParam),
        sortOption,
        setSearchParams,
    };
}
